import { AxiosInstance } from 'axios'
import {
  DataExport,
  DataExportResponse,
  DataExportsParams,
  DataExportsResponse,
  ExportEntityParams,
  GetDataExportsParams,
  GetDataExportsResponse,
  TaxReport
} from 'types/exports'
import { DATA_EXPORTS_ENDPOINTS } from 'api/ApiRoutes'
import dayjs from 'utils/dayjs'
import pickBy from 'lodash.pickby'
import { cleanOnboardingParams, formatDateParams } from 'utils/Functions'
import { DateRangeFilterInAPI } from 'types/reports'
import { MembersFilter, MembersTableColumnsKeys } from 'types/members'

class ExportsApi {
  constructor(private axiosInstance: AxiosInstance) {}

  exportData = async (params: Partial<ExportEntityParams>) => {
    const d = await this.axiosInstance.post<DataExportResponse>(
      DATA_EXPORTS_ENDPOINTS.BASE,
      params
    )
    return d.data
  }

  getDataExports = async ({
    view = 'personal',
    category
  }: GetDataExportsParams) => {
    const d = await this.axiosInstance.get<GetDataExportsResponse>(
      DATA_EXPORTS_ENDPOINTS.BASE,
      {
        params: category ? { category } : undefined
      }
    )
    let data = [] as DataExport[]
    if (view === 'personal') {
      data = d.data.exports.sort((a: DataExport, b: DataExport) =>
        dayjs(b.last_modified).diff(dayjs(a.last_modified))
      )
    }
    if (view === 'team') {
      data = d.data.enterprise.sort((a_1: DataExport, b_2: DataExport) =>
        dayjs(b_2.last_modified).diff(dayjs(a_1.last_modified))
      )
    }
    return {
      data,
      totals: d.data.totals ?? undefined
    }
  }

  dataExport = async ({
    type,
    dataExport,
    columns,
    file_type
  }: DataExportsParams) => {
    const { date_range, ...rest } = dataExport ?? {}
    const payload = pickBy(
      {
        ...rest,
        ...formatDateParams<DateRangeFilterInAPI>(date_range)
      },
      (v) => {
        if (Array.isArray(v)) return v.length > 0
        return v && v !== undefined && v !== null
      }
    )
    const d = await this.axiosInstance.post<DataExportsResponse>(
      DATA_EXPORTS_ENDPOINTS.BASE,
      {
        type: type,
        data_export: payload,
        columns: columns,
        file_type: file_type
      }
    )
    return d.data
  }

  membersExport = async ({
    fetchParams,
    columns,
    file_type,
    filters
  }: {
    columns?: MembersTableColumnsKeys[]
    file_type?: 'excel' | 'csv'
    filters?: MembersFilter
    fetchParams?: {
      member_token_ids?: string
      onboarding?: boolean
      include_invites?: boolean
      archived?: boolean
    }
  }) => {
    const d = await this.axiosInstance.post<DataExportsResponse>(
      DATA_EXPORTS_ENDPOINTS.BASE,
      {
        type: 'members',
        fetch_params: {
          ...fetchParams,
          ...(filters ? cleanOnboardingParams(filters) : {})
        },
        columns: columns,
        file_type: file_type
      }
    )
    return d.data
  }

  getTaxReport = async ({
    start_date,
    end_date,
    include_report_identifier = false
  }: {
    start_date: string
    end_date: string
    include_report_identifier?: boolean
  }) => {
    const d = await this.axiosInstance.get<TaxReport>(
      DATA_EXPORTS_ENDPOINTS.TAX_REPORT,
      {
        params: {
          include_report_identifier,
          start_date,
          end_date
        }
      }
    )
    return d.data
  }

  createTeamTaxReport = async ({
    team_id,
    start_date,
    end_date,
    include_report_identifier = false
  }: {
    team_id: string
    start_date: string
    end_date: string
    include_report_identifier?: boolean
  }) => {
    const d = await this.axiosInstance.post<TaxReport>(
      DATA_EXPORTS_ENDPOINTS.TEAM_TAX_REPORT,
      {
        team_id,
        include_report_identifier,
        start_date,
        end_date
      }
    )
    return d.data
  }
}

export default ExportsApi
